import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import { PaymentDevicePickupArea } from '@/models/common/PaymentDeviceEnums';
import { PaymentDeviceRentalRequestDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { CreatePaymentDeviceRentalRequestDtoOut } from '@/models/out/PaymentDeviceDtoOut';
import { fundDevicesUrl, paymentDeviceErrorMessage, todayInIsrael } from '@/helpers/paymentDevices';
import { formatFeeAmount } from '@/helpers/paymentDeviceFees';
import usePaymentDeviceApi from '@/pages/Management/PaymentDevices/usePaymentDeviceApi';
import { DateField, DialogActions, ErrorLine, NoteField } from '@/pages/Management/PaymentDevices/dialogs/dialogParts';

interface RentalRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
  fundId: number;
  fundName?: string;
  /** The fund already has a request waiting: a second one is allowed (one device per request), but said. */
  hasPendingRequest: boolean;
  /** The rental fee per business day when fees are on, said before the coordinator asks; null while off. */
  feeRatePerDay?: number | null;
}

const AREAS = [PaymentDevicePickupArea.Israel, PaymentDevicePickupArea.US, PaymentDevicePickupArea.Both];

/** Whole days from start to the expected return, or null while either date is missing or out of order. */
const rentalDays = (start: string, end: string): number | null => {
  if (!start || !end) return null;
  const days = Math.round((Date.parse(end) - Date.parse(start)) / 86_400_000);
  return days > 0 ? days : null;
};

/**
 * «בקשה להשכרת מכשיר»: one terminal per request, for the dates and the area the fund will collect
 * donations in (Israel, the US or both). It is not where the terminal is picked up: that is always
 * the office.
 */
const RentalRequestDialog = ({ open, onClose, onSubmitted, fundId, fundName, hasPendingRequest, feeRatePerDay = null }: RentalRequestDialogProps) => {
  const { t } = useTranslation();
  const api = usePaymentDeviceApi();
  const today = todayInIsrael();

  const [startDate, setStartDate] = useState(today);
  const [expectedReturnDate, setExpectedReturnDate] = useState('');
  const [pickupArea, setPickupArea] = useState<PaymentDevicePickupArea>(PaymentDevicePickupArea.Israel);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const days = rentalDays(startDate, expectedReturnDate);

  useEffect(() => {
    if (!open) return;
    setStartDate(today);
    setExpectedReturnDate('');
    setPickupArea(PaymentDevicePickupArea.Israel);
    setNotes('');
    setError(null);
  }, [open, today]);

  const handleSubmit = async () => {
    if (!startDate || !expectedReturnDate) {
      setError(t('paymentDevices.dialogs.datesRequired'));
      return;
    }
    if (startDate < today) {
      setError(t('paymentDevices.coordinator.form.startNotPast'));
      return;
    }
    if (expectedReturnDate <= startDate) {
      setError(t('paymentDevices.dialogs.returnAfterStart'));
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      await api.post<PaymentDeviceRentalRequestDtoIn, CreatePaymentDeviceRentalRequestDtoOut>(`${fundDevicesUrl(fundId)}/requests`, {
        startDate,
        expectedReturnDate,
        pickupArea,
        notes: notes.trim() || null,
      });
      onSubmitted();
      onClose();
    } catch (err) {
      setError(paymentDeviceErrorMessage(err, t, 'paymentDevices.errors.save'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose(); }}
      title={t('paymentDevices.coordinator.form.title')}
      maxWidth="560px"
      fitContent
    >
      <div className="flex flex-col gap-5 p-4 w-full box-border">
        <p className="m-0 text-sm text-(--font-body-2)">
          {t('paymentDevices.coordinator.form.fundLine', { fundId, fundName: fundName ?? '' })}
        </p>

        {feeRatePerDay != null && (
          <p className="m-0 rounded-lg bg-(--surface-grey-lowest) border border-(--border-divider) px-3.5 py-3 text-sm leading-relaxed">
            {t('paymentDevices.fees.rateLine', { rate: formatFeeAmount(feeRatePerDay) })}
          </p>
        )}

        {hasPendingRequest && (
          <p className="m-0 rounded-lg bg-[#fff4e5] text-[#6b3a00] px-3.5 py-3 text-sm leading-relaxed">
            {t('paymentDevices.coordinator.form.pendingWarning')}
          </p>
        )}

        <fieldset className="flex flex-col gap-2 border-0 m-0 p-0">
          <legend className="p-0 mb-2 text-[15px] font-semibold">{t('paymentDevices.coordinator.form.period')}</legend>
          <div className="flex gap-4">
            <DateField id="pd-request-start" label={t('paymentDevices.coordinator.form.startDate')} value={startDate} onChange={setStartDate} min={today} />
            <DateField
              id="pd-request-return"
              label={t('paymentDevices.coordinator.form.expectedReturnDate')}
              value={expectedReturnDate}
              onChange={setExpectedReturnDate}
              min={startDate || today}
            />
          </div>
          {days && <span className="text-[13px] text-(--font-body-3)">{t('paymentDevices.coordinator.form.days', { count: days })}</span>}
        </fieldset>

        <fieldset className="border-0 m-0 p-0">
          <legend className="p-0 mb-2 text-[15px] font-semibold">{t('paymentDevices.coordinator.form.pickupArea')}</legend>
          <div className="grid grid-cols-3 gap-3">
            {AREAS.map((area) => {
              const isSelected = pickupArea === area;
              return (
                <label
                  key={area}
                  className={`flex items-center gap-2 px-3.5 h-12 rounded-[10px] cursor-pointer text-[15px] font-semibold ${isSelected ? 'border-2 border-(--border-primary) bg-(--surface-primary-lowest)' : 'border border-(--border-main) bg-white'}`}
                >
                  <input
                    type="radio"
                    name="pd-request-area"
                    value={area}
                    checked={isSelected}
                    onChange={() => setPickupArea(area)}
                    className="m-0 accent-(--surface-primary)"
                  />
                  {t(`paymentDevices.pickupArea.${area}`)}
                </label>
              );
            })}
          </div>
        </fieldset>

        <NoteField id="pd-request-notes" label={t('paymentDevices.coordinator.form.notes')} value={notes} onChange={setNotes} />
        <p className="m-0 rounded-lg bg-(--surface-tertiary-low) text-[#09474e] px-3.5 py-3 text-sm leading-relaxed">
          {t('paymentDevices.coordinator.pickupNotice')}
        </p>
        <ErrorLine error={error} />
        <DialogActions
          onCancel={onClose}
          onConfirm={handleSubmit}
          isSubmitting={isSubmitting}
          confirmText={t('paymentDevices.coordinator.form.submit')}
        />
      </div>
    </MainDialogLayout>
  );
};

export default RentalRequestDialog;
